import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { UnfoldLess, UnfoldMore } from '@mui/icons-material';
import {
  DataGridPremium,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridColDef,
  GridColumnGroupingModel,
  GridDensity,
  gridRowTreeSelector,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { ConditionsNew } from '@retail/calculus/types';
import {
  DATA_GRID_ID,
  getDataGridLocalization,
  getInitialDataGridDensityFromLocalStorage,
  MgPrisLocale,
} from '@retail/utils';
import { NoTableRowsOverlay, TruncatedText } from '@retail/components';
import {
  dataGridColorRowClass,
  dataGridNoHorizontalPaddingClass,
  dataGridRowHoverClass,
} from '@shared/styles';
import { useGroupedByTreeRowClassName } from '@retail/hooks';
import { buildConditionTreePaths } from '@retail/calculus/utils';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { SupplierGroupToggle } from '../AllConditions/SupplierGroupToggle';
import { SupplierLevelAndNameCell } from '../AllConditions/SupplierLevelAndNameCell';
import { baseColumnProps, getDescendantCondition } from '../AllConditions/utils';
import { LevelChip } from '../ConditionApproval';

export const conditionsTreeDataGridInputCellClass = 'MuiDataGrid-cell--input';

interface Props {
  dataGridId: DATA_GRID_ID;
  rows: ConditionsNew[];
  getRowId: (row: ConditionsNew) => string;
  columns: GridColDef<ConditionsNew>[];
  columnGroupingModel?: GridColumnGroupingModel;
  language: string;
  loading?: boolean;
  noRowsText: string;
  getAssortmentLabel: (row: ConditionsNew) => string;
  levelColumnWidth?: number;
  renderRowActions?: (row: ConditionsNew) => React.ReactNode;
  density?: GridDensity;
}

export function ConditionsTreeDataGrid({
  rows,
  getRowId,
  columns,
  columnGroupingModel,
  language,
  loading,
  noRowsText,
  getAssortmentLabel,
  levelColumnWidth = 500,
  renderRowActions,
  density: densityProp,
  dataGridId,
}: Props) {
  const density = densityProp ?? getInitialDataGridDensityFromLocalStorage(dataGridId);
  const t = useCalculusTFunction();
  const apiRef = useGridApiRef();
  const getRowClassName = useGroupedByTreeRowClassName<ConditionsNew>(apiRef);

  const treePaths = useMemo(() => buildConditionTreePaths(rows, getRowId), [rows, getRowId]);
  const getTreeDataPath = useCallback(
    (row: ConditionsNew) => treePaths.get(getRowId(row)) ?? [getRowId(row)],
    [treePaths, getRowId]
  );

  const [allExpanded, setAllExpanded] = useState(true);
  const [isScrolledHorizontally, setIsScrolledHorizontally] = useState(false);

  useEffect(
    () =>
      apiRef.current?.subscribeEvent('scrollPositionChange', ({ left }) =>
        setIsScrolledHorizontally(left > 0)
      ),
    [apiRef]
  );

  const toggleExpandAll = useCallback(() => {
    const next = !allExpanded;
    Object.values(gridRowTreeSelector(apiRef)).forEach((node) => {
      if (node.type === 'group') apiRef.current?.setRowChildrenExpansion(node.id, next);
    });
    setAllExpanded(next);
  }, [allExpanded, apiRef]);

  const levelColumn: GridColDef<ConditionsNew> = useMemo(
    () => ({
      ...baseColumnProps,
      field: 'levelAndName',
      headerName: t('calculus.allConditions.levelAndName'),
      width: levelColumnWidth,
      renderHeader: () => (
        <Stack direction="row" alignItems="center" gap={4.5}>
          <Tooltip title={allExpanded ? t('calculus.collapseAll') : t('calculus.expandAll')}>
            <IconButton size="small" onClick={toggleExpandAll}>
              {allExpanded ? <UnfoldLess /> : <UnfoldMore />}
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle2" component="span" fontWeight="600">
            {t('calculus.allConditions.levelAndName')}
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        const { row, rowNode } = params;

        const renderContent = () => {
          if (rowNode.type === 'group' && rowNode.isAutoGenerated) {
            const supplier = getDescendantCondition(params)?.supplier;
            if (!supplier) return null;
            return (
              <SupplierLevelAndNameCell supplierName={supplier.name} conditionInDraft={false} />
            );
          }
          if (row.bindingType === 'MG_PARTICIPANT_ACCOUNT') {
            return (
              <SupplierLevelAndNameCell
                supplierName={row.supplier?.name ?? row.bindingTargetName}
                conditionInDraft={row.inDraft}
              />
            );
          }
          if (row.bindingType === 'ASSORTMENT') {
            return (
              <Stack direction="row" gap={2} alignItems="center">
                <LevelChip
                  bindingType="ASSORTMENT"
                  active={!row.inDraft}
                  variant="fixed"
                  size="small"
                  abbreviation
                />
                <Typography variant="subtitle2" component="span">
                  {getAssortmentLabel(row)}
                </Typography>
              </Stack>
            );
          }
          return (
            <Stack direction="row" gap={2} alignItems="center" minWidth={0}>
              <Box flexShrink={0}>
                <LevelChip
                  bindingType={row.bindingType}
                  bindingTargetItem={row.bindingTargetItem}
                  active={!row.inDraft}
                  variant="fixed"
                  size="small"
                  hideGtin
                  abbreviation
                />
              </Box>
              <TruncatedText
                variant="subtitle2"
                component="span"
                text={`${row.bindingTargetName} (${row.bindingTargetId})`}
              />
            </Stack>
          );
        };

        const content = renderContent();
        if (!content) return null;

        return (
          <Stack
            direction="row"
            marginLeft={rowNode.depth * 2}
            gap={1}
            alignItems="center"
            minWidth={0}
          >
            <Box sx={{ width: 64, flexShrink: 0, display: 'flex' }}>
              <SupplierGroupToggle params={params} />
            </Box>
            {content}
          </Stack>
        );
      },
    }),
    [allExpanded, getAssortmentLabel, levelColumnWidth, t, toggleExpandAll]
  );

  const actionsColumn: GridColDef<ConditionsNew> = useMemo(
    () => ({
      ...baseColumnProps,
      field: 'actions',
      headerName: '',
      width: 64,
      align: 'center',
      renderCell: (params) => {
        // No actions on auto-generated supplier group rows (they have no condition of their own).
        if (params.rowNode.type === 'group' && params.rowNode.isAutoGenerated) return null;
        return renderRowActions?.(params.row);
      },
    }),
    [renderRowActions]
  );

  const allColumns = useMemo(
    () => (renderRowActions ? [levelColumn, ...columns, actionsColumn] : [levelColumn, ...columns]),
    [levelColumn, columns, renderRowActions, actionsColumn]
  );

  return (
    <Stack flexGrow={1} gap={1}>
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <DataGridPremium
            className={dataGridNoHorizontalPaddingClass}
            apiRef={apiRef}
            columns={allColumns}
            rows={rows}
            treeData
            defaultGroupingExpansionDepth={-1}
            getTreeDataPath={getTreeDataPath}
            columnGroupingModel={columnGroupingModel}
            columnVisibilityModel={{ [GRID_TREE_DATA_GROUPING_FIELD]: false }}
            initialState={{
              pinnedColumns: {
                left: ['levelAndName'],
                right: renderRowActions ? ['actions'] : [],
              },
            }}
            getRowId={getRowId}
            getRowClassName={getRowClassName}
            getRowHeight={() => 'auto'}
            density={density}
            loading={loading}
            disableRowSelectionOnClick
            disableColumnResize
            disableColumnReorder
            disableColumnMenu
            hideFooter
            localeText={getDataGridLocalization(language as MgPrisLocale)}
            sx={(theme) => ({
              '& .MuiDataGrid-cell': { py: cellPaddingY[density] },
              [`& .MuiDataGrid-cell.${conditionsTreeDataGridInputCellClass}`]: { p: 0 },
              '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-filler--pinnedLeft': {
                backgroundColor: 'inherit !important',
              },
              '& .MuiDataGrid-filler--pinnedLeft': {
                zIndex: -1,
              },
              '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-filler--pinnedLeft':
                {
                  clipPath: 'inset(0px -8px 0px 0px)',
                  boxShadow: isScrolledHorizontally ? '4px 0 6px 0 rgba(0, 0, 0, 0.2)' : 'none',
                  transition: 'box-shadow 0.2s',
                },
              [`& .MuiDataGrid-row.${dataGridRowHoverClass}:hover`]: {
                zIndex: 35,
                '& .MuiDataGrid-cell--pinnedLeft': {
                  boxShadow: isScrolledHorizontally
                    ? `4px 0 6px 0 rgba(0, 0, 0, 0.2), ${theme.shadows[1]}`
                    : theme.shadows[1],
                },
              },
              [`& .MuiDataGrid-row.${dataGridColorRowClass}.${dataGridColorRowClass}:not(:hover)`]:
                {
                  backgroundColor: `${theme.vars.palette.grey[50]} !important`,
                },
              [`& .MuiDataGrid-row.${dataGridColorRowClass} + .MuiDataGrid-row.${dataGridColorRowClass} .MuiDataGrid-cell`]:
                {
                  borderTopColor: theme.vars.palette.grey.A100,
                },
            })}
            slots={{
              noRowsOverlay: () => <NoTableRowsOverlay noRowsText={noRowsText} />,
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
}

const cellPaddingY: Record<GridDensity, number> = {
  compact: 0,
  standard: 0.5,
  comfortable: 1.5,
};
